import React, { useState } from 'react';
import WhatsAppPopup from '@/components/layout/WhatsAppPopup';
import { Link } from 'react-router-dom';
import { MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '@/lib/useLanguage';
import GestosAgeisSEO from '@/components/seo/GestosAgeisSEO';
import GestosAgeisContent from '@/components/seo/GestosAgeisContent';
import GestosAgeisIntentPopup from '@/components/layout/GestosAgeisIntentPopup';
import PopularEquipmentLinks from '@/components/seo/PopularEquipmentLinks';
import WhyBuyInStore from '@/components/seo/WhyBuyInStore';
import ComplaintsBookButton from '@/components/layout/ComplaintsBookButton';
import OrthocareHeroSearch from '@/components/landing/OrthocareHeroSearch';
import { getGaPageStrings, getGaPageTestimonials, getGaPageFaqs } from '@/components/landing/gestosAgeisPageI18n';

function FAQSection({ language }) {
  const [openIndex, setOpenIndex] = useState(null);
  const items = getGaPageFaqs(language);
  const s = getGaPageStrings(language);
  return (
    <section className="py-8 bg-gray-50 px-4">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-4 text-center">
          {s.faqTitle}
        </h2>
        <div className="space-y-2">
          {items.map((item, i) =>
          <div key={i} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <button
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              className="w-full flex items-center justify-between px-4 py-3.5 text-left hover:bg-gray-50 transition-colors">
                <span className="font-semibold text-gray-800 text-sm pr-4">{item.q}</span>
                {openIndex === i ? <ChevronUp className="w-4 h-4 text-blue-600 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
              </button>
              {openIndex === i &&
            <div className="px-4 pb-4 pt-1 bg-blue-50 border-t border-blue-100">
                <p className="text-gray-700 text-sm leading-relaxed">{item.a}</p>
              </div>
            }
            </div>
          )}
        </div>
        <div className="text-center mt-5">
          <a href="/faq" className="text-blue-600 hover:underline text-sm font-medium">
            {s.seeAllFaqs}
          </a>
        </div>
      </div>
    </section>);
}

export default function GestosAgeisPresentationRent() {
  const { lang: language } = useLanguage();

  const trans = getGaPageStrings(language);

  const heroI18n = {
    pt: { h1: 'Aluguer de Material Ortopédico em Lisboa | Gestos Ágeis', heroP: 'A qualquer hora, entregue em sua casa ou recolha em loja.', trust1: 'Recolha em loja gratuita', trust2: 'Apoio 24 horas via WhatsApp', trust3: 'Equipamento de elevada qualidade' },
    en: { h1: 'Rent here! Your equipment just a click away', heroP: 'Anytime — delivered to your home or pick up in store.', trust1: 'Free in-store pickup', trust2: '24h support via WhatsApp', trust3: 'High-quality equipment' },
    es: { h1: '¡Alquile aquí! Su equipo a un click de distancia', heroP: 'En cualquier momento, entregado en casa o recogido en tienda.', trust1: 'Recogida en tienda gratuita', trust2: 'Soporte 24h por WhatsApp', trust3: 'Equipamiento de alta calidad' },
    fr: { h1: 'Louez ici ! Votre équipement à un clic', heroP: 'À tout moment, livré chez vous ou à retirer en magasin.', trust1: 'Retrait en magasin gratuit', trust2: 'Support 24h via WhatsApp', trust3: 'Équipement de haute qualité' },
    it: { h1: 'Noleggia qui! Il tuo equipaggiamento a un click di distanza', heroP: 'In qualsiasi momento, consegnato a casa o ritiro in negozio.', trust1: 'Ritiro in negozio gratuito', trust2: 'Supporto 24h via WhatsApp', trust3: 'Attrezzatura di alta qualità' },
    de: { h1: 'Hier mieten! Ihre Ausrüstung nur einen Klick entfernt', heroP: 'Jederzeit — geliefert nach Hause oder Abholung im Geschäft.', trust1: 'Kostenlose Abholung im Geschäft', trust2: '24h Support via WhatsApp', trust3: 'Hochwertige Ausrüstung' },
  };
  const heroContent = heroI18n[language] || heroI18n.pt;

  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const currentTestimonials = getGaPageTestimonials(language);

  const featuredCities = [
  { name: 'Lisboa', image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/514b6bf41_images.jpeg' },
  { name: 'Porto', image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/c84c2d466_17750779733243039880391257164712.jpg' },
  { name: 'Braga', image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a17ad0f78_17750780553527638477717099787799.jpg' },
  { name: 'Covilhã', image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/603c6293d_1775078155913733176041155689769.jpg' },
  { name: 'Cascais', image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/d0a7fd84b_17750781947918316063259178449606.jpg' },
  { name: 'Aveiro', image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/4261228c6_17750782417964655631148216950851.jpg' }];

  return (
    <div className="min-h-screen bg-white">
      <GestosAgeisSEO language={language || 'pt'} />
      <GestosAgeisIntentPopup />

      {/* Hero + Pesquisa (design verde Gestos Ágeis) */}
      <OrthocareHeroSearch lang={language || 'pt'} forceGestos hero={heroContent} />

      {/* SEO content: intro, categories, store */}
      <GestosAgeisContent language={language || 'pt'} />

      {/* Best Sellers */}
      <section className="py-6 px-3 sm:px-6 max-w-7xl mx-auto">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-3">{trans.bestSellers}</h2>
        <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-4">
          {featuredCities.map((city) =>
          <Link key={city.name} to={`/SearchResults?city=${encodeURIComponent(city.name)}&source=gestosageis`}
          className="relative cursor-pointer overflow-hidden rounded-lg shadow h-24 sm:h-40 lg:h-52 block group">
              <img src={city.image} alt={`Aluguer equipamento ortopédico ${city.name}`} className="w-full h-full object-cover" loading="lazy" decoding="async" width="400" height="200" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-2">
                <h3 className="text-xs sm:text-base font-bold text-white">{city.name}</h3>
              </div>
            </Link>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-8 bg-white px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-6 text-center">
            {trans.testimonialsTitle}
          </h2>
          <div className="relative">
            <div className="bg-gray-50 rounded-2xl p-6 shadow-sm border border-gray-100 min-h-[160px] flex flex-col justify-between">
              <div>
                <div className="flex mb-3">
                  {Array.from({ length: currentTestimonials[testimonialIndex].stars }).map((_, i) =>
                  <span key={i} className="text-yellow-400 text-lg">★</span>
                  )}
                  {Array.from({ length: 5 - currentTestimonials[testimonialIndex].stars }).map((_, i) =>
                  <span key={i} className="text-gray-300 text-lg">★</span>
                  )}
                </div>
                <p className="text-gray-700 text-sm sm:text-base italic leading-relaxed">"{currentTestimonials[testimonialIndex].text}"</p>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                  {currentTestimonials[testimonialIndex].name[0]}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{currentTestimonials[testimonialIndex].name}</p>
                  <p className="text-xs text-gray-500">{currentTestimonials[testimonialIndex].city}</p>
                </div>
              </div>
            </div>
            {/* Navigation */}
            <div className="flex items-center justify-center gap-4 mt-4">
              <button
                onClick={() => setTestimonialIndex((i) => (i - 1 + currentTestimonials.length) % currentTestimonials.length)}
                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition text-lg font-bold">
                ‹
              </button>
              <div className="flex gap-1 items-center">
                {currentTestimonials.map((_, i) =>
                <button
                  key={i}
                  onClick={() => setTestimonialIndex(i)}
                  aria-label={`Testemunho ${i + 1}`}
                  className="min-w-[28px] min-h-[28px] flex items-center justify-center rounded-full transition-all">
                  <span className={`block rounded-full transition-all ${i === testimonialIndex ? 'w-4 h-2 bg-blue-600' : 'w-2 h-2 bg-gray-300'}`} />
                </button>
                )}
              </div>
              <button
                onClick={() => setTestimonialIndex((i) => (i + 1) % currentTestimonials.length)}
                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition text-lg font-bold">
                ›
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection language={language} />

      {/* Stats */}
      <section className="py-6 bg-gray-50 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-2xl sm:text-4xl font-bold text-blue-600">60+</div>
            <p className="text-xs text-gray-600 mt-0.5 font-medium">{trans.cities}</p>
          </div>
          <div>
            <div className="text-2xl sm:text-4xl font-bold text-green-600">1k</div>
            <p className="text-xs text-gray-600 mt-0.5 font-medium">{trans.rentalDaysYear}</p>
          </div>
          <div>
            <div className="text-2xl sm:text-4xl font-bold text-blue-600">2k+</div>
            <p className="text-xs text-gray-600 mt-0.5 font-medium">{trans.customers}</p>
          </div>
        </div>
      </section>

      {/* Porquê comprar na loja física */}
      <WhyBuyInStore accentColor="#0e6b4f" />

      {/* CTA */}
      <section className="py-8 bg-gradient-to-r from-blue-600 to-green-500 text-white text-center px-4">
        <h2 className="text-xl sm:text-3xl font-bold mb-2">{trans.readyToRent}</h2>
        <p className="text-xs sm:text-base opacity-90 mb-4">{trans.startJourney}</p>
      </section>

      {/* Contact Bar */}
      <section className="bg-[#1a2235] py-6 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 bg-[#2a3448] border border-[#cebe75]/40 text-[#cebe75] text-xs font-semibold px-3 py-1.5 rounded-full mb-2">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
            {trans.support24h}
          </span>
          <p className="text-gray-400 text-xs mb-4">
            {trans.availableAnytime}
          </p>
          <div className="flex flex-col sm:grid sm:grid-cols-3 gap-2">
            <a href="tel:+351216035281" className="bg-[#232d42] hover:bg-[#2a3448] transition rounded-xl p-3 text-left flex items-center gap-3">
              <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              <div>
                <p className="text-gray-400 text-xs">{trans.phone24h}</p>
                <p className="text-white font-bold text-sm leading-tight truncate">+351 216035281</p>
                <p className="text-gray-500 text-xs">{trans.localRateCall}</p>
              </div>
            </a>
            <a href="https://wa.me/351216035281" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 transition rounded-xl p-3 flex items-center justify-center gap-3">
              <svg className="w-7 h-7 text-white shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
              <div className="text-left">
                <p className="text-white/80 text-xs">WhatsApp 24h</p>
                <p className="text-white font-bold text-base leading-tight">{trans.message}</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      <PopularEquipmentLinks language={language || 'pt'} />

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-6 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <div className="col-span-2 sm:col-span-1">
            <img src="https://media.base44.com/images/public/691220492173ae6cbe68ab07/93a4b2fb8_1777227440051110206008173168802.png" alt="Gestos Ágeis" className="h-12 w-auto object-contain mb-2 brightness-0 invert" />
            <a href="/AboutUs" className="text-white font-bold mb-1 text-sm hover:text-[#cebe75] transition-colors block">{trans.aboutUs}</a>
            <a href="/faq" className="text-[#cebe75] text-sm hover:opacity-80 transition-colors block mb-1">{trans.faq}</a>
            <p className="text-xs">{trans.leadingOrtho}</p>
          </div>
          <div>
            <h3 className="text-white font-bold mb-1 text-sm">{trans.services}</h3>
            <ul className="space-y-0.5 text-xs">
              <li><a href="/SearchResults" className="hover:text-[#cebe75] transition-colors">{trans.equipmentRental}</a></li>
              <li><a href="/aluguer/categoria/cadeiras-de-rodas" className="hover:text-[#cebe75] transition-colors">{trans.wheelchairs}</a></li>
              <li><a href="/aluguer/categoria/andarilhos" className="hover:text-[#cebe75] transition-colors">{trans.walkers}</a></li>
              <li><a href="/aluguer/categoria/camas-articuladas" className="hover:text-[#cebe75] transition-colors">{trans.hospitalBeds}</a></li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-bold mb-1 text-sm">{trans.legal}</h3>
            <ul className="space-y-0.5 text-xs">
              <li><a href="/privacy" className="hover:text-[#cebe75] transition-colors">{trans.privacy}</a></li>
              <li><a href="/terms" className="hover:text-[#cebe75] transition-colors">{trans.terms}</a></li>
              <li><a href="/cookies" className="hover:text-[#cebe75] transition-colors">{trans.cookies}</a></li>
            </ul>
            <div className="mt-3">
              <ComplaintsBookButton height={42} />
            </div>
          </div>
          <div>
            <h3 className="text-white font-bold mb-1 text-sm">{trans.contact}</h3>
            <p className="text-xs">geral@gestosageis.com</p>
            <p className="text-xs mt-0.5">+351 216 035 281</p>
          </div>
        </div>
        <div className="border-t border-gray-700 pt-3 text-center text-xs">
          {trans.copyright}
          <p className="mt-1 text-gray-500">
            {language === 'en' ?
            'Gestos Ágeis Unipessoal Lda – NIF: 515948039' :
            'Gestos Ágeis Unipessoal Lda – NIF: 515948039'
            }
          </p>
        </div>
      </footer>

      <WhatsAppPopup lang={language || 'pt'} />

      {/* WhatsApp Button */}
      <a href="https://wa.me/351216035281" target="_blank" rel="noopener noreferrer"
      className="fixed bottom-24 right-4 w-14 h-14 bg-green-500 text-white rounded-full shadow-xl hover:bg-green-600 flex items-center justify-center z-50 animate-bounce" style={{ boxShadow: '0 0 0 0 rgba(37,211,102,0.4)', animation: 'whatsapp-pulse 2s infinite' }}>
      <style>{`@keyframes whatsapp-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(37,211,102,0.5);transform:scale(1);} 50%{box-shadow:0 0 0 12px rgba(37,211,102,0);transform:scale(1.08);} }`}</style>
        <MessageCircle className="w-7 h-7" />
      </a>
    </div>);
}